import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useFormikContext } from 'formik';
import { Grid } from '@material-ui/core';
import { lookupsJson, StructureCategory } from '@ourbranch/lookups';

import { getHeatingOptionsByOccupancy } from 'common/helpers/farm-helpers';
import Field from 'core/components/form/form.v2';
import { Label } from 'core/components/label';
import { useStore } from 'core/store';
import { LabelTooltip } from 'core/components/label-tooltip';
import { propDetailsCodes } from '../quote-forms/quote-forms.constants';
import useStyles from './initial-risk-detail.styles';

// Form options constants
const bqOptions = lookupsJson.farm.buildingQuality;
const ctOptions = lookupsJson.farm.agriculturalConstructionType;
const barnRtOptions = lookupsJson.farm.barnRoofType;
const occupancyUseTypeByStructureCategory = lookupsJson.farm.occupancyUseTypeByStructureCategory;
const buildingClassOptions = lookupsJson.farm.buildingClassOptions;
const buildingClassOptionsByStructureCategory = lookupsJson.farm.buildingClassOptionsByStructureCategory;

const FO6RiskDetail = () => {
  const classes = useStyles();
  const {
    quote: { getAlerts }
  } = useStore();

  const { values, isSubmitting, setFieldValue } = useFormikContext();
  const alerts = getAlerts(propDetailsCodes, values.policyType);
  const has5004Alert = alerts.some((alert) => alert.code === 5004);
  const initialRiskDetailErrors = useRef(null);
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);

  // Extract frequently accessed nested values to improve readability
  const farmLocation = values.farmLocations?.[0];
  const farmRisk = farmLocation?.risks?.[0];
  const farmLocationPath = 'farmLocations[0]';
  const farmRiskPath = `${farmLocationPath}.risks[0]`;
  const buildingPath = `${farmRiskPath}.building`;
  const riskAddressPath = `${farmRiskPath}.address`;

  const toggleAdditionalFields = useCallback(() => {
    setShowAdditionalFields(!showAdditionalFields);
  }, [showAdditionalFields]);

  useEffect(() => {
    if (alerts.length && initialRiskDetailErrors.current && isSubmitting) {
      initialRiskDetailErrors.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }, [alerts, isSubmitting]);

  useEffect(() => {
    if (has5004Alert) {
      setShowAdditionalFields(true);
    } else {
      setShowAdditionalFields(false);
    }
  }, [has5004Alert]);

  useEffect(() => {
    const useApplicantAddress = farmRisk?.useApplicantAddress;
    if (useApplicantAddress && values.address) {
      setFieldValue(`${riskAddressPath}.address`, values.address);
      setFieldValue(`${riskAddressPath}.city`, values.city);
      setFieldValue(`${riskAddressPath}.state`, values.state);
      setFieldValue(`${riskAddressPath}.zip`, values.zip);
      // County is set in the primary location details component
      setFieldValue(`${riskAddressPath}.county`, farmLocation?.address?.county);
    } else {
      setFieldValue(`${riskAddressPath}.address`, undefined);
      setFieldValue(`${riskAddressPath}.city`, undefined);
      setFieldValue(`${riskAddressPath}.state`, undefined);
      setFieldValue(`${riskAddressPath}.zip`, undefined);
      setFieldValue(`${riskAddressPath}.county`, undefined);
    }
  }, [
    farmRisk?.useApplicantAddress,
    values.address,
    values.city,
    values.state,
    values.zip,
    farmLocation?.address?.county,
    setFieldValue,
    riskAddressPath
  ]);

  // Structure categories depend on state, so we need to get them dynamically
  const structureCategories = lookupsJson.farm.structureCategoriesByState[values.state];

  // Check if structure category is Barns/Outbuildings, Storage Grain, Poultry Building, or Radio/TV Tower
  const isBarnOutbuilding = farmRisk?.building?.buildingType === StructureCategory.BarnsOutbuildings;
  const isStorageGrain = farmRisk?.building?.buildingType === StructureCategory.SilosGrainBins;

  const isPoultryBuilding = [
    StructureCategory.PoultryBrooder,
    StructureCategory.PoultryIncubator,
    StructureCategory.PoultryOther
  ].includes(farmRisk?.building?.buildingType);

  const currentHeatingOptions = getHeatingOptionsByOccupancy(
    farmRisk?.building?.buildingType,
    farmRisk?.building?.occupancy
  );

  const isSelectedHeatingSourceUnitCountBased = currentHeatingOptions.find(
    (option) => option.id === farmRisk?.building?.primaryHeatingSource
  )?.unitCountBased;

  // Check field requirements based on occupancy type
  const isTowerOccupancy = farmRisk?.building?.occupancy === 'GHT';
  const shouldEnableDiameterHeight = isTowerOccupancy;
  const shouldEnableSqFt = farmRisk?.building?.occupancy && !isTowerOccupancy;

  return (
    <Grid container spacing={2}>
      {values.state && (
        <Field
          xs={12}
          type="select"
          id={`${buildingPath}.buildingType`}
          name={`${buildingPath}.buildingType`}
          mode="light"
          label="Structure Category"
          options={structureCategories}
          onChange={(val) => {
            if (['PB', 'PI', 'PO'].includes(val)) {
              setFieldValue(`${buildingPath}.occupancy`, 'PCH');
            }
          }}
        />
      )}

      {isBarnOutbuilding && (
        <>
          <Field
            xs={6}
            type="select"
            id={`${buildingPath}.occupancy`}
            name={`${buildingPath}.occupancy`}
            mode="light"
            label="Occupancy or use type"
            options={occupancyUseTypeByStructureCategory[farmRisk?.building?.buildingType]}
          />
          <Field
            xs={6}
            type="numeric"
            id={`${buildingPath}.yearBuilt`}
            name={`${buildingPath}.yearBuilt`}
            mode="light"
            label="Year built in"
            format={{
              allowNegative: false
            }}
          />
          <Field
            xs={4}
            type="numeric"
            id={`${buildingPath}.sqFt`}
            name={`${buildingPath}.sqFt`}
            mode="light"
            label="Total sqft"
          />
          <Field
            xs={4}
            type="select"
            id={`${buildingPath}.buildingQuality`}
            name={`${buildingPath}.buildingQuality`}
            mode="light"
            label="Quality"
            options={bqOptions}
          />
          <Field
            xs={4}
            type="select"
            id={`${buildingPath}.roofShape`}
            name={`${buildingPath}.roofShape`}
            mode="light"
            label="Roof shape"
            options={barnRtOptions}
          />
          <Field
            xs={4}
            type="select"
            id={`${buildingPath}.constructionType`}
            name={`${buildingPath}.constructionType`}
            mode="light"
            label="Construction type"
            options={ctOptions}
          />
          <Field
            xs={4}
            type="select"
            id={`${buildingPath}.buildingClass`}
            name={`${buildingPath}.buildingClass`}
            mode="light"
            label="Building class"
            options={buildingClassOptionsByStructureCategory[farmRisk?.building?.buildingType] || buildingClassOptions}
          />
          <Field
            xs={3}
            type="checkbox"
            id={`${buildingPath}.structureOpenSides`}
            name={`${buildingPath}.structureOpenSides`}
            mode="light"
            label="Has open sides?"
            className={classes.openSidesCheckbox}
            disabled={![2, 3].includes(farmRisk?.building?.buildingClass)}
          />
          <Grid item xs={4}>
            <LabelTooltip
              label="Solid Fuel Burning Appliances"
              tooltip={{
                label: 'More Info',
                onHoverText: `Count appliances that burn fuel like gas, propane, oil, or wood. Common examples: furnace, boiler, water heater, fireplace, wood stove, gas oven, gas dryer, or backup generators. Electric-only appliances don't count.`
              }}
              mode="light"
            >
              <Field
                type="numeric"
                id="farmRisk.building.numSolidFuelBurningAppliances"
                name={`${buildingPath}.numSolidFuelBurningAppliances`}
                mode="light"
                format={{
                  allowNegative: false
                }}
              />
            </LabelTooltip>
          </Grid>
          <Field
            xs={4}
            type="select"
            id={`${buildingPath}.primaryHeatingSource`}
            name={`${buildingPath}.primaryHeatingSource`}
            mode="light"
            fast={false}
            label="Primary heating"
            options={currentHeatingOptions}
            truncateSelectedValue
          />
          <Field
            xs={4}
            type="numeric"
            id={`${buildingPath}.numHeatingSources`}
            name={`${buildingPath}.numHeatingSources`}
            mode="light"
            label="Heating units"
            disabled={!isSelectedHeatingSourceUnitCountBased}
            helperText="Acceptable range: 1-1000"
          />
        </>
      )}

      {isStorageGrain && (
        <>
          <Field
            xs={6}
            type="select"
            id={`${buildingPath}.occupancy`}
            name={`${buildingPath}.occupancy`}
            mode="light"
            label="Occupancy or use type"
            options={occupancyUseTypeByStructureCategory[farmRisk?.building?.buildingType]}
          />
          <Field
            xs={6}
            type="numeric"
            id={`${buildingPath}.yearBuilt`}
            name={`${buildingPath}.yearBuilt`}
            mode="light"
            label="Year built in"
            format={{
              allowNegative: false
            }}
          />
          <Field
            xs={4}
            type="numeric"
            id={`${buildingPath}.diameter`}
            name={`${buildingPath}.diameter`}
            mode="light"
            label="Diameter (ft)"
            disabled={!shouldEnableDiameterHeight}
          />
          <Field
            xs={4}
            type="numeric"
            id={`${buildingPath}.height`}
            name={`${buildingPath}.height`}
            mode="light"
            label="Height (ft)"
          />
          <Field
            xs={4}
            type="numeric"
            id={`${buildingPath}.sqFt`}
            name={`${buildingPath}.sqFt`}
            mode="light"
            label="Total sqft"
            disabled={!shouldEnableSqFt}
          />
          <Field
            xs={6}
            type="select"
            id={`${buildingPath}.buildingQuality`}
            name={`${buildingPath}.buildingQuality`}
            mode="light"
            label="Quality"
            options={bqOptions}
          />
          <Field
            xs={6}
            type="select"
            id={`${buildingPath}.constructionType`}
            name={`${buildingPath}.constructionType`}
            mode="light"
            label="Construction type"
            options={ctOptions}
          />
          <Field
            xs={6}
            type="select"
            id={`${buildingPath}.buildingClass`}
            name={`${buildingPath}.buildingClass`}
            mode="light"
            label="Building class"
            options={buildingClassOptionsByStructureCategory[farmRisk?.building?.buildingType] || buildingClassOptions}
          />
          <Field
            xs={6}
            type="numeric"
            id={`${buildingPath}.bushelCapacity`}
            name={`${buildingPath}.bushelCapacity`}
            mode="light"
            label="Bushel capacity"
          />
        </>
      )}

      {isPoultryBuilding && (
        <>
          <Field
            xs={4}
            type="numeric"
            id={`${buildingPath}.yearBuilt`}
            name={`${buildingPath}.yearBuilt`}
            mode="light"
            label="Year built in"
            format={{
              allowNegative: false
            }}
          />
          <Field
            xs={4}
            type="numeric"
            id={`${buildingPath}.sqFt`}
            name={`${buildingPath}.sqFt`}
            mode="light"
            label="Total sqft"
          />
          <Field
            xs={4}
            type="select"
            id={`${buildingPath}.buildingQuality`}
            name={`${buildingPath}.buildingQuality`}
            mode="light"
            label="Quality"
            options={bqOptions}
          />
          <Field
            xs={4}
            type="select"
            id={`${buildingPath}.constructionType`}
            name={`${buildingPath}.constructionType`}
            mode="light"
            label="Construction type"
            options={ctOptions}
          />
          <Field
            xs={4}
            type="numeric"
            id={`${buildingPath}.numSolidFuelBurningAppliances`}
            name={`${buildingPath}.numSolidFuelBurningAppliances`}
            mode="light"
            label="Number of solid fuel burn appls"
          />
          <Field
            xs={4}
            type="select"
            id={`${buildingPath}.primaryHeatingSource`}
            name={`${buildingPath}.primaryHeatingSource`}
            mode="light"
            label="Primary heating"
            options={currentHeatingOptions}
            truncateSelectedValue
          />
          <Field
            xs={4}
            type="numeric"
            id={`${buildingPath}.numHeatingSources`}
            name={`${buildingPath}.numHeatingSources`}
            mode="light"
            label="Heating units"
            disabled={!isSelectedHeatingSourceUnitCountBased}
            helperText="Acceptable range: 1-1000"
          />
        </>
      )}

      {values.address && (
        <Grid item xs={12} alignItems="flex-start" className={classes.useApplicantAddressLabel}>
          <Field
            type="checkbox"
            id={`${farmRiskPath}.useApplicantAddress`}
            name={`${farmRiskPath}.useApplicantAddress`}
            mode="light"
            fast={false}
            label={
              <span>
                Use <b>{[values.address, values.city, values.state, values.zip].filter(Boolean).join(', ')}</b> as the
                address for this risk
              </span>
            }
          />
        </Grid>
      )}
      {!farmRisk?.useApplicantAddress && values.address && (
        <>
          <Field
            xs={6}
            type="address"
            id={`${riskAddressPath}.address`}
            name={`${riskAddressPath}.address`}
            mode="light"
            label="Address"
          />
          <Field
            xs={6}
            type="string"
            id={`${riskAddressPath}.address2`}
            name={`${riskAddressPath}.address2`}
            mode="light"
            label="Address 2"
          />
          <Field
            xs={4}
            type="string"
            id={`${riskAddressPath}.city`}
            name={`${riskAddressPath}.city`}
            mode="light"
            label="City"
          />
          <Field
            xs={4}
            type="string"
            id={`${riskAddressPath}.state`}
            name={`${riskAddressPath}.state`}
            mode="light"
            label="State"
          />
          <Field
            xs={4}
            type="string"
            id={`${riskAddressPath}.zip`}
            name={`${riskAddressPath}.zip`}
            mode="light"
            label="Zip Code"
          />
        </>
      )}

      {has5004Alert && (
        <Grid item xs={12} className={classes.checkboxContainer}>
          <Label type="action" onClick={toggleAdditionalFields}>
            {showAdditionalFields ? 'Hide additional fields' : 'Show additional fields'}
          </Label>
        </Grid>
      )}
    </Grid>
  );
};

export default FO6RiskDetail;
